import React, { useEffect, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import api, { fmtInt } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useShowPrices } from "@/showPrices";
import { Loading } from "@/components/Bits";
import { ArrowsClockwise, Fire, FireSimple, Snowflake, Clock } from "@phosphor-icons/react";

const COLUMNS = [
  { id: "Past/Now", titleFr: "Past / Now", titleEn: "Past / Now",
    color: "#ED2027", soft: "rgba(237,32,39,0.06)", Icon: Fire },
  { id: "Hot",      titleFr: "Hot (≤2 ans)", titleEn: "Hot (≤2y)",
    color: "#fb923c", soft: "rgba(251,146,60,0.07)", Icon: FireSimple },
  { id: "Warm",     titleFr: "Warm (3–5 ans)", titleEn: "Warm (3–5y)",
    color: "#eab308", soft: "rgba(234,179,8,0.07)", Icon: Clock },
  { id: "Cold",     titleFr: "Cold (>5 ans)", titleEn: "Cold (>5y)",
    color: "#38bdf8", soft: "rgba(56,189,248,0.07)", Icon: Snowflake },
];

export default function KanbanBoard({ StatusPill }) {
  const { t, lang } = useI18n();
  const { fmt: fmtMoney } = useCurrency();
  const { showPrices } = useShowPrices();
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/pipeline/board").then((r) => setBoard(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;
    const card = board.columns[source.droppableId].find((c) => c.id === draggableId);
    if (!card) return;
    // Optimistic update
    setBoard((prev) => {
      const next = { ...prev, columns: { ...prev.columns } };
      next.columns[source.droppableId] = prev.columns[source.droppableId].filter((c) => c.id !== draggableId);
      next.columns[destination.droppableId] = [
        { ...card, effective_stage: destination.droppableId, is_override: true },
        ...prev.columns[destination.droppableId],
      ];
      const totals = { ...prev.totals };
      const v = card.active_method || 0;
      const q = card.total_qty || 0;
      totals[source.droppableId] = {
        count: prev.totals[source.droppableId].count - 1,
        value: prev.totals[source.droppableId].value - v,
        qty: (prev.totals[source.droppableId].qty || 0) - q,
      };
      totals[destination.droppableId] = {
        count: prev.totals[destination.droppableId].count + 1,
        value: prev.totals[destination.droppableId].value + v,
        qty: (prev.totals[destination.droppableId].qty || 0) + q,
      };
      next.totals = totals;
      return next;
    });
    try {
      setBusy(true);
      await api.put("/pipeline/stage", { opp_key: card.opp_key, stage: destination.droppableId });
      toast.success(lang === "fr"
        ? `Déplacé : ${card.operator} → ${destination.droppableId}`
        : `Moved: ${card.operator} → ${destination.droppableId}`);
    } catch (e) {
      toast.error(lang === "fr" ? "Échec du déplacement" : "Move failed");
      load(); // rollback by reloading server truth
    } finally {
      setBusy(false);
    }
  };

  const onResetCard = async (oppKey) => {
    try {
      await api.delete(`/pipeline/stage/${encodeURIComponent(oppKey)}`);
      toast.success(lang === "fr" ? "Reclassement annulé" : "Override cleared");
      load();
    } catch {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  if (!board) return <Loading />;

  return (
    <div data-testid="kanban-board" className="space-y-4">
      <div className="flex items-center justify-between text-[12px]" style={{ color: "var(--text-tertiary)" }}>
        <div className="font-mono">
          {lang === "fr"
            ? `Glissez-déposez les cartes pour reclasser. ${board.won_count} gagnée(s) · ${board.lost_count} perdue(s) — exclues.`
            : `Drag cards to reclassify. ${board.won_count} won · ${board.lost_count} lost — excluded.`}
        </div>
        {busy && <span className="font-mono animate-pulse">↻ {lang === "fr" ? "Synchronisation…" : "Syncing…"}</span>}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMNS.map((col) => {
            const cards = board.columns[col.id] || [];
            const total = board.totals[col.id] || { count: 0, value: 0 };
            const Icon = col.Icon;
            return (
              <Droppable droppableId={col.id} key={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    data-testid={`kanban-col-${col.id.replace("/", "-")}`}
                    className="rounded-[14px] p-3 flex flex-col"
                    style={{
                      background: snapshot.isDraggingOver ? col.soft : "var(--bg-elevated)",
                      border: `1px solid ${snapshot.isDraggingOver ? col.color : "var(--border)"}`,
                      minHeight: 380,
                      maxHeight: "70vh",
                      transition: "background 120ms, border 120ms",
                    }}
                  >
                    {/* Column header */}
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <Icon size={15} weight="fill" style={{ color: col.color }} />
                        <span className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
                          {lang === "fr" ? col.titleFr : col.titleEn}
                        </span>
                      </div>
                      <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-full"
                            style={{ background: col.soft, color: col.color }}>
                        {total.count}
                      </span>
                    </div>
                    <div className="text-[11px] font-mono mb-2 px-1" style={{ color: "var(--text-tertiary)" }}>
                      {showPrices
                        ? <>Σ {fmtMoney(total.value)}</>
                        : <>Σ {fmtInt(total.qty || 0)} {lang === "fr" ? "unités" : "units"}</>}
                    </div>

                    {/* Cards */}
                    <div className="flex-1 overflow-y-auto pr-1 space-y-2">
                      {cards.length === 0 && (
                        <div className="text-center text-[11px] py-8 italic" style={{ color: "var(--text-tertiary)" }}>
                          {lang === "fr" ? "Déposez ici" : "Drop here"}
                        </div>
                      )}
                      {cards.slice(0, 50).map((c, idx) => (
                        <Draggable draggableId={c.id} index={idx} key={c.id}>
                          {(prov, snap) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              data-testid={`kanban-card-${c.id}`}
                              className="rounded-[10px] p-2.5 select-none"
                              style={{
                                background: "var(--bg)",
                                border: `1px solid ${c.is_override ? col.color : "var(--border)"}`,
                                boxShadow: snap.isDragging ? "0 8px 24px rgba(0,0,0,0.15)" : "none",
                                transform: prov.draggableProps.style?.transform,
                                ...prov.draggableProps.style,
                              }}
                            >
                              <div className="flex items-start justify-between gap-1.5">
                                <div className="min-w-0">
                                  <div className="text-[12.5px] font-medium truncate" style={{ color: "var(--text-primary)" }}>
                                    {c.operator}
                                  </div>
                                  <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-secondary)" }}>
                                    {c.city} · {c.sub_system}
                                  </div>
                                </div>
                                {c.is_override && (
                                  <button
                                    onClick={() => onResetCard(c.opp_key)}
                                    data-testid={`kanban-reset-${c.id}`}
                                    title={lang === "fr" ? "Revenir au statut auto" : "Reset to computed"}
                                    className="p-0.5 -mr-1 rounded hover:bg-[var(--bg-subtle)] transition-colors"
                                    style={{ color: col.color }}
                                  >
                                    <ArrowsClockwise size={12} weight="bold" />
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[10.5px] font-mono" style={{ color: "var(--text-tertiary)" }}>
                                  {c.id} · OVH {c.next_ovh || "—"}
                                </span>
                                <span className="text-[11.5px] font-mono font-semibold" style={{ color: col.color }}>
                                  {showPrices
                                    ? fmtMoney(c.active_method || 0)
                                    : `${fmtInt(c.total_qty || 0)} ${lang === "fr" ? "u." : "u."}`}
                                </span>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {cards.length > 50 && (
                        <div className="text-center text-[11px] py-2 font-mono" style={{ color: "var(--text-tertiary)" }}>
                          +{cards.length - 50} {lang === "fr" ? "autres masquées" : "more hidden"}
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
